// features/public-filters/domain/mappers/bedMapper.ts
import { Range } from '../FilterTypes';
import { bedValueMap } from '../filterOptions';

export type BedOptionLabel = 'Any' | '1' | '2' | '3' | '4' | '5' | '6+';

/**
 * Given a clicked beds option + current selection, return:
 * - the new Range to store in filter
 * - the new internal selection array (e.g. [2,4] for 2–4)
 */
export function applyBedsClick(
  option: BedOptionLabel,
  currentSelection: number[],
): { range: Range; nextSelection: number[] } {
  // Any → unbounded
  if (option === 'Any') {
    return {
      range: Range.create(null, null),
      nextSelection: [],
    };
  }

  const value = bedValueMap[option];

  // Special case: 6+ → [6, ∞)
  if (option === '6+') {
    return {
      range: Range.create(6, null),
      nextSelection: [ value ],
    };
  }

  // If one value already selected, second click makes a range
  if (currentSelection.length === 1) {
    const [ first ] = currentSelection;
    const min = Math.min(first, value);
    const max = Math.max(first, value);

    return {
      range: Range.create(min, max),
      nextSelection: [ first, value ],
    };
  }

  // Otherwise single-value range (e.g. exactly 3 beds)
  return {
    range: Range.create(value, value),
    nextSelection: [ value ],
  };
}

/**
 * Given a Range and an option label, decide if that option is “selected”.
 */
export function isBedOptionSelected(
  option: BedOptionLabel,
  range: Range | null,
  index: number,
): boolean {
  // No range → “Any” is selected
  if (!range || range.min == null) {
    return index === 0; // assume index 0 is "Any"
  }

  if (option === 'Any') return false;

  const value = bedValueMap[option];
  const min = range.min ?? -Infinity;
  const max = range.max ?? Infinity;

  return value >= min && value <= max;
}
